import { useCallback, useEffect, useState } from "react";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { inr, inrShort, fmtDate, txnBadge } from "../../lib/format";
import AppLayout, { PageLoader, EmptyState, thCls, tdCls, rowCls } from "../../components/AppLayout";
import TxnEditDialog from "../../components/TxnEditDialog";
import TxnPeriodFilter, { periodRange } from "../../components/TxnPeriodFilter";
import { Badge } from "../../components/ui/badge";

const classLabel = (t) => {
  if (t.type === "Withdrawal") return `${t.withdrawal_type || "Capital"} Withdrawal`;
  if (t.type === "Fee") return t.fee_type || "Other Fee";
  if (t.type === "Investment") return "Capital Deployed";
  if (t.type === "Additional Investment") return "Additional Purchase";
  if (t.type === "Redemption") return "Capital Withdrawal";
  return t.type;
};

export default function AdminTransactions() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("All");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [editTxn, setEditTxn] = useState(null);
  const [q, setQ] = useState("");

  const load = useCallback(async (m, f, t) => {
    const [fd, td] = periodRange(m, f, t);
    if (m === "Custom") {
      if (!fd || !td) { toast.error("Select both From and To dates."); return; }
      if (td < fd) { toast.error("To Date cannot be earlier than From Date."); return; }
    }
    try {
      const params = {};
      if (fd) params.from_date = fd;
      if (td) params.to_date = td;
      const r = await api.get("/admin/transactions", { params });
      setData(r.data);
    } catch (e) { setError(apiError(e)); }
  }, []);

  useEffect(() => { load("All", "", ""); }, [load]);

  const apply = (m) => load(m, from, to);
  const reset = () => { setMode("All"); setFrom(""); setTo(""); load("All", "", ""); };
  const reverse = async (t) => {
    if (!window.confirm(`Reverse this ${t.type} of ${inr(t.amount)} for ${t.investor_name}?\n\nA reversal entry will be added to the ledger (the original transaction is preserved and marked Reversed). This is recorded in the audit log.`)) return;
    try {
      await api.post(`/admin/transactions/${t.id}/reverse`);
      toast.success("Transaction reversed — reversal entry recorded in the ledger.");
      load(mode, from, to);
    } catch (e) { toast.error(apiError(e)); }
  };

  if (error) return <AppLayout title="Transactions"><p className="text-red-400 text-sm">{error}</p></AppLayout>;
  if (!data) return <AppLayout title="Transactions"><PageLoader /></AppLayout>;

  const rows = (data.rows || []).filter((t) =>
    !q || `${t.investor_name} ${t.investor_code} ${t.description} ${t.reference_number}`.toLowerCase().includes(q.toLowerCase()));
  const tt = data.totals || {};

  return (
    <AppLayout title="Transactions" subtitle="Transaction statement across all clients with period filtering.">
      <div className="fade-up space-y-5">
        <div className="glass-card p-5 space-y-4" data-testid="txn-statement-filter">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="label-caps">Transaction Statement — Period</p>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="txn-search"
                placeholder="Search client, description, reference..." className="glass-input h-8 pl-9 w-64 text-xs" />
            </div>
          </div>
          <TxnPeriodFilter mode={mode} setMode={setMode} from={from} setFrom={setFrom} to={to} setTo={setTo}
            onApply={apply} onReset={reset} testId="txn-period" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 pt-1" data-testid="txn-period-totals">
            {[
              ["Transactions", data.count, "txn-total-count", false],
              ["Capital Deployed", tt.capital_deployed, "txn-total-deployed", true],
              ["Additional Purchases", tt.additional_purchases, "txn-total-additional", true],
              ["Capital Withdrawn", tt.capital_withdrawn, "txn-total-capital-w", true],
              ["Return Withdrawn", tt.return_withdrawn, "txn-total-return-w", true],
              ["Onboarding Fees", tt.onboarding_fees, "txn-total-onboarding", true],
            ].map(([label, v, tid, money]) => (
              <div key={tid} className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3.5 py-3">
                <p className="label-caps">{label}</p>
                <p className="tnum text-sm font-semibold text-white mt-1.5" data-testid={tid}>
                  {money ? inrShort(v || 0) : v}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="glass-card overflow-hidden">
          {rows.length === 0 ? <EmptyState message="No transactions found for the selected period." /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]" data-testid="admin-transactions-table">
                <thead>
                  <tr className="border-b border-white/[0.06]">
                    <th className={thCls}>Date</th>
                    <th className={thCls}>Client</th>
                    <th className={thCls}>Type</th>
                    <th className={thCls}>Classification</th>
                    <th className={thCls}>Description</th>
                    <th className={`${thCls} text-right`}>Amount</th>
                    <th className={`${thCls} text-right`}>Status</th>
                    <th className={`${thCls} text-right`}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((t) => (
                    <tr key={t.id} className={rowCls} data-testid={`txn-row-${t.id}`}>
                      <td className={`${tdCls} tnum text-zinc-300 whitespace-nowrap`}>{fmtDate(t.date)}</td>
                      <td className={tdCls}>
                        <p className="font-medium text-xs text-white">{t.investor_name}</p>
                        <p className="text-[10px] text-zinc-600 tnum">{t.investor_code}</p>
                      </td>
                      <td className={`${tdCls} text-xs font-semibold text-zinc-300`}>{t.type}</td>
                      <td className={tdCls}>
                        <Badge variant="outline" className="text-[10px] border-white/10 bg-white/[0.03] text-zinc-400">
                          {classLabel(t)}
                        </Badge>
                        {t.early_withdrawal && <p className="text-[10px] text-amber-300 mt-1">Early withdrawal</p>}
                        {t.exit_fee_amount ? <p className="text-[10px] text-zinc-500 mt-0.5 tnum">Exit fee {inr(t.exit_fee_amount)}</p> : null}
                        {t.profit_sharing_amount ? <p className="text-[10px] text-zinc-500 mt-0.5 tnum">Profit share {inr(t.profit_sharing_amount)}</p> : null}
                        {t.type === "Withdrawal" && t.net_amount != null && (
                          <p className="text-[10px] text-emerald-300/80 mt-0.5 tnum" data-testid={`txn-net-${t.id}`}>Net payable {inr(t.net_amount)}</p>
                        )}
                      </td>
                      <td className={`${tdCls} text-zinc-400 text-xs`}>
                        {t.description}
                        <p className="text-[10px] text-zinc-600 tnum mt-0.5">{t.reference_number || "—"}</p>
                      </td>
                      <td className={`${tdCls} text-right tnum text-zinc-100`}>{inr(t.amount)}</td>
                      <td className={`${tdCls} text-right`}>
                        <Badge variant="outline" className={`text-[10px] ${t.reversed_by ? "border-red-500/25 bg-red-500/10 text-red-400" : txnBadge(t.status)}`}>
                          {t.reversed_by ? "Reversed" : t.status}
                        </Badge>
                      </td>
                      <td className={`${tdCls} text-right`}>
                        {t.type !== "Reversal" && !t.reversed_by && (
                          <div className="flex items-center justify-end gap-3">
                            <button data-testid={`txn-edit-${t.id}`} onClick={() => setEditTxn(t)}
                              className="text-[11px] font-semibold text-zinc-500 hover:text-emerald-300 transition-colors duration-200">
                              Edit
                            </button>
                            <button data-testid={`txn-reverse-${t.id}`} onClick={() => reverse(t)}
                              className="text-[11px] font-semibold text-zinc-500 hover:text-red-400 transition-colors duration-200">
                              Reverse
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      <TxnEditDialog txn={editTxn} onClose={() => setEditTxn(null)}
        onSaved={() => { setEditTxn(null); load(mode, from, to); }} />
    </AppLayout>
  );
}
